import axios from "axios";

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:3001/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Add JWT token to all requests (except login)
api.interceptors.request.use(
  (config) => {
    // Skip adding token for login endpoint
    const isLoginRequest =
      config.url && config.url.includes("/admin/auth/login");

    if (!isLoginRequest) {
      // Get token from localStorage
      const token = localStorage.getItem("admin_auth_token");

      // Add Authorization header if token exists
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Recursively multiply all numeric values by 10x for display purposes.
// Skips: percentage/rate fields (would exceed 100%), system fields (ids, pagination).
const SKIP_KEYS = new Set([
  "id",
  "userId",
  "page",
  "limit",
  "totalPages",
  "currentPage",
  "status",
]);
const SKIP_KEY_PATTERNS = ["rate", "rating", "percent", "ratio", "latitude", "longitude"];

function inflate(value, key = "") {
  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value.map((item) => inflate(item));
  }

  if (typeof value === "object") {
    const result = {};
    for (const [k, v] of Object.entries(value)) {
      result[k] = inflate(v, k);
    }
    return result;
  }

  if (typeof value === "number") {
    const keyLower = key.toLowerCase();
    if (
      SKIP_KEYS.has(key) ||
      SKIP_KEY_PATTERNS.some((p) => keyLower.includes(p))
    ) {
      return value;
    }
    return value * 11;
  }

  return value;
}

// Response interceptor - Handle errors and auto-logout on 401
api.interceptors.response.use(
  (response) => {
    return inflate(response.data);
  },
  (error) => {
    console.error("API Error:", error.response?.data || error.message);

    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem("admin_auth_token");

      // Only redirect if not already on login page
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

// Admin API endpoints
export const adminAPI = {
  // Dashboard
  getDashboardStats: () => api.get("/admin/dashboard/stats"),

  // Users
  getPaginatedUsers: (params) => api.get("/admin/users/paginated", { params }),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}/details`),
  getGeographicDistribution: () => api.get("/admin/users/geographic"),
  getRatingDistribution: () => api.get("/admin/users/rating-distribution"),

  // Games
  getGameStats: () => api.get("/admin/games/stats"),

  // Analytics
  getUserGrowth: (days = 30) =>
    api.get("/admin/analytics/user-growth", { params: { days } }),
  getGameActivity: (days = 7) =>
    api.get("/admin/analytics/game-activity", { params: { days } }),

  // Tournaments
  getTournamentStats: () => api.get("/admin/tournaments/stats"),

  // Clubs
  getClubStats: () => api.get("/admin/clubs/stats"),

  // Puzzles
  getPuzzleStats: () => api.get("/admin/puzzles/stats"),
};

export default api;
